"use client";

import type { CSSProperties } from "react";
import { useMemo, useState } from "react";
import { ServiceIcon, type ServiceIconName } from "../components/service-icons";
import { SiteFooter, SiteHeader, UtilityBar } from "../components/site-chrome";
import { LocationCards } from "../components/location-cards";

const plans = [
  {
    name: "Standard",
    price: "$11.39",
    tone: "Entry level",
    cpu: "2 CPU",
    disk: "20 GB SSD",
    ram: "2 GB RAM",
    ip: "1 IP",
    bandwidth: "1 TB",
    fit: "Development servers and small websites",
    meter: 28,
  },
  {
    name: "Business",
    price: "$16.99",
    tone: "Balanced",
    cpu: "2 CPU",
    disk: "40 GB SSD",
    ram: "4 GB RAM",
    ip: "1 IP",
    bandwidth: "1 TB",
    fit: "Production websites, client portals, APIs",
    meter: 46,
  },
  {
    name: "Pro",
    price: "$39.99",
    tone: "Application hosting",
    cpu: "3 CPU",
    disk: "80 GB SSD",
    ram: "6 GB RAM",
    ip: "1 IP",
    bandwidth: "1 TB",
    fit: "Online stores and application servers",
    meter: 68,
  },
  {
    name: "Elite",
    price: "$55.99",
    tone: "Higher capacity",
    cpu: "4 CPU",
    disk: "120 GB SSD",
    ram: "8 GB RAM",
    ip: "1 IP",
    bandwidth: "1 TB",
    fit: "Larger applications and multiple services",
    meter: 88,
  },
  {
    name: "Ultra",
    price: "$99.99",
    tone: "Ultra",
    cpu: "6 Cores",
    disk: "240 GB",
    ram: "16 GB",
    ip: "2 IP",
    bandwidth: "2 TB",
    fit: "High-throughput apps, heavy traffic, multi-service workloads",
    meter: 108,
  },
];

const cloudFlow = [
  { icon: "network", tone: "blue", title: "Pick a Location", copy: "Choose the datacenter closest to your users." },
  { icon: "cpu", tone: "green", title: "Select Resources", copy: "Choose CPU, memory, and storage for your applications." },
  { icon: "server", tone: "cyan", title: "Choose the Image", copy: "Select the operating system for your virtual server." },
  { icon: "pcie", tone: "violet", title: "Choose your IP and Bandwidth", copy: "Set the addressing and transfer your project needs." },
  { icon: "power", tone: "rose", title: "Go Live", copy: "Configure your applications using full root access." },
];

const cloudOps = [
  {
    title: "Auto Deployment",
    copy: "Your Cloud Node is imaged and brought online automatically after checkout, ready in minutes.",
    icon: "rocket",
    tone: "green",
  },
  {
    title: "Custom Control",
    copy: "Reboot, reload, and adjust IPs on demand through our custom control panel.",
    icon: "hardware",
    tone: "amber",
  },
];

const cloudLocations = [
  { code: "LAX1", city: "Los Angeles" },
  { code: "HKG1", city: "Hong Kong" },
  { code: "TYO1", city: "Tokyo" },
];

export function CloudNodePageClient() {
  const stepSize = 20;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [locationIndex, setLocationIndex] = useState(0);
  const sliderValue = useMemo(() => (selectedIndex + 1) * stepSize, [selectedIndex]);
  const selectedPlan = plans[selectedIndex];
  // Range thumb is inset by half its width (34px) at each end, so align the
  // track fill with the dot center rather than using a plain percentage.
  const railFill = useMemo(
    () => `calc((100% - 34px) * ${sliderValue / 100} + 17px)`,
    [sliderValue],
  );
  const selectedSpecs = [
    {
      label: "CPU",
      value: selectedPlan.cpu,
      meta: "Virtual CPU allocation",
      icon: "cpu" as ServiceIconName,
      tone: "blue",
    },
    {
      label: "Storage",
      value: selectedPlan.disk,
      meta: "SSD storage",
      icon: "server" as ServiceIconName,
      tone: "amber",
    },
    {
      label: "Memory",
      value: selectedPlan.ram,
      meta: "Allocated memory",
      icon: "memory" as ServiceIconName,
      tone: "green",
    },
    {
      label: "IP Address",
      value: selectedPlan.ip,
      meta: "IP allocation",
      icon: "network" as ServiceIconName,
      tone: "violet",
    },
    {
      label: "Bandwidth",
      value: selectedPlan.bandwidth,
      meta: "500 Mbps",
      icon: "pcie" as ServiceIconName,
      tone: "cyan",
    },
  ];

  return (
    <div className="host-sketch cloud-node-shell product-shell">
      <input className="theme-toggle-input" type="checkbox" id="theme-toggle" defaultChecked aria-label="Toggle dark mode" />
      <div className="stage product-stage">
        <SiteHeader />
        <UtilityBar />

        <main>
          <section className="cloud-hero">
            <div className="cloud-hero-copy">
              <h1>Cloud Node Hosting</h1>
              <p>
                KVM virtual servers with allocated CPU and memory, SSD storage, and full root access.
              </p>
              <div className="cloud-hero-actions">
                <a className="btn primary" href="#cloud-builder">View Plans</a>
                <a className="btn secondary" href="#cloud-flow">Deployment</a>
              </div>
            </div>

            <div className="cloud-rack-visual" aria-hidden="true">
              <div className="cloud-shape cloud-shape-main">
                <div className="cloud-visual-status">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="cloud-plan-chip">
                  <b>KVM</b>
                </div>
              </div>
              <div className="cloud-shape cloud-shape-small cloud-shape-a">
                <span></span>
                <span></span>
              </div>
              <div className="cloud-shape cloud-shape-small cloud-shape-b">
                <span></span>
                <span></span>
              </div>
              <div className="cloud-network-path cloud-network-path-a"></div>
              <div className="cloud-network-path cloud-network-path-b"></div>
              <div className="cloud-network-path cloud-network-path-c"></div>
              <div className="cloud-edge-cable cloud-edge-cable-a"></div>
              <div className="cloud-edge-cable cloud-edge-cable-b"></div>
            </div>
          </section>

          <section className="cloud-builder" id="cloud-builder">
            <div className="cloud-builder-head">
              <div>
                <h2>Cloud Node plans</h2>
              </div>
              <div className="cloud-live-price">
                <small>Starts at</small>
                <b>{selectedPlan.price}</b>
                <em>/mo</em>
              </div>
            </div>

            <div className="cloud-range-wrap">
              <div className="cloud-range-track" style={{ "--rail-fill": railFill } as CSSProperties}></div>
              <input
                aria-label="Cloud Node plan size"
                className="cloud-range"
                max={plans.length * stepSize}
                min={0}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  const nextIndex = Math.max(0, Math.min(plans.length - 1, Math.round(value / stepSize) - 1));
                  setSelectedIndex(nextIndex);
                }}
                step={stepSize}
                type="range"
                value={sliderValue}
              />
              <div className="cloud-plan-markers">
                {plans.map((plan, index) => (
                  <button
                    className={index === selectedIndex ? "active" : ""}
                    key={plan.name}
                    onClick={() => setSelectedIndex(index)}
                    type="button"
                  >
                    <b>{plan.name}</b>
                    <span>{plan.price}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="cloud-selected-grid">
              <article className="cloud-selected-card">
                <div className="cloud-selected-top">
                  <div>
                    <span>Datacenter</span>
                    <h3>{cloudLocations[locationIndex].city}</h3>
                  </div>
                  <div className="cloud-loc-picker" role="group" aria-label="Datacenter location">
                    {cloudLocations.map((loc, index) => (
                      <button
                        className={index === locationIndex ? "active" : ""}
                        key={loc.code}
                        onClick={() => setLocationIndex(index)}
                        type="button"
                      >
                        {loc.code}
                      </button>
                    ))}
                  </div>
                </div>
                <a href="/contact-us" className="cloud-order">Contact Us</a>
              </article>

              <div className="cloud-spec-grid" aria-label={`${selectedPlan.name} specifications`}>
                {selectedSpecs.map((spec) => (
                  <article className={`cloud-spec-card cloud-spec-card-${spec.tone}`} key={spec.label}>
                    <ServiceIcon name={spec.icon} tone={spec.tone as "blue" | "green" | "amber" | "cyan" | "violet"} />
                    <h3>{spec.label}</h3>
                    <b>{spec.value}</b>
                    <span>{spec.meta}</span>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="cloud-flow" id="cloud-flow">
            <div className="cloud-flow-strip">
              {cloudFlow.map(({ tone, title, copy }) => (
                <div className={`cloud-flow-cell cloud-flow-cell-${tone}`} key={title}>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </div>
              ))}
            </div>
          </section>

          <LocationCards />

          <section className="faq-section">
            <div className="section-heading">
              <h2>Frequently asked questions</h2>
            </div>
            <div className="faq-split">
              <div className="faq-list">
                <details>
                  <summary>What is a KVM Cloud Node?</summary>
                  <p>A KVM Cloud Node is an isolated virtual server with allocated CPU, memory, storage, and networking.</p>
                </details>
                <details>
                  <summary>Can I increase resources later?</summary>
                  <p>You can increase resources by upgrading to a larger plan in the same category.</p>
                </details>
                <details>
                  <summary>Are backups included?</summary>
                  <p>You are responsible for backups, either through your panel tools or a remote backup service.</p>
                </details>
              </div>
              <div className="faq-ops">
                {cloudOps.map((item) => (
                  <div className="operation-card" key={item.title}>
                    <div className="operation-card-head">
                      <ServiceIcon name={item.icon as ServiceIconName} tone={item.tone as "green" | "amber"} />
                      <h3>{item.title}</h3>
                    </div>
                    <p>{item.copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </main>

        <SiteFooter />
      </div>
    </div>
  );
}
